import dayjs, { Dayjs } from 'dayjs';
import { DateFormats } from '@/constants/dates';
import { DatePresetKey } from '@/models/cancelledOrders.model';
import { IAdminMainMetricsQuery, MainMetricsGroupBy } from '@/models/dashboard.model';
import { assertUnreachable } from '@/utils';

export interface IDatePresetRange {
  preset: DatePresetKey;
  dateFrom: Dayjs | null;
  dateTo: Dayjs | null;
}

export const DEFAULT_MAIN_METRICS_PRESET: DatePresetKey = 'current_month';

export const ADMIN_MAIN_METRICS_PRESETS: DatePresetKey[] = [
  'all_time',
  'previous_year',
  'current_month',
  'last_month',
  'last_7_days',
  'last_30_days',
];

export const resolveDatePreset = (
  preset: DatePresetKey,
  customFrom?: Dayjs | null,
  customTo?: Dayjs | null,
): IDatePresetRange => {
  const today = dayjs();

  switch (preset) {
    case 'all_time':
      return { preset, dateFrom: null, dateTo: null };
    case 'current_year':
      return { preset, dateFrom: today.startOf('year'), dateTo: today };
    case 'previous_year': {
      const previousYear = today.subtract(1, 'year');
      return {
        preset,
        dateFrom: previousYear.startOf('year'),
        dateTo: previousYear.endOf('year'),
      };
    }
    case 'last_month': {
      const lastMonth = today.subtract(1, 'month');
      return {
        preset,
        dateFrom: lastMonth.startOf('month'),
        dateTo: lastMonth.endOf('month'),
      };
    }
    case 'current_month':
      return { preset, dateFrom: today.startOf('month'), dateTo: today };
    case 'last_7_days':
      return { preset, dateFrom: today.subtract(6, 'day').startOf('day'), dateTo: today };
    case 'last_30_days':
      return { preset, dateFrom: today.subtract(29, 'day').startOf('day'), dateTo: today };
    case 'custom':
      return { preset, dateFrom: customFrom ?? null, dateTo: customTo ?? null };
    default:
      return assertUnreachable(preset);
  }
};

export const toAdminMainMetricsQuery = (
  range: IDatePresetRange,
  groupBy: MainMetricsGroupBy = 'day',
): IAdminMainMetricsQuery => {
  const query: IAdminMainMetricsQuery = { group_by: groupBy };

  if (range.dateFrom) {
    query.date_from = range.dateFrom.format(DateFormats.DATE_YEAR_MONTH_DAY);
  }
  if (range.dateTo) {
    query.date_to = range.dateTo.format(DateFormats.DATE_YEAR_MONTH_DAY);
  }

  return query;
};

export const resolveGroupByForRange = (
  preset: DatePresetKey,
  dateFrom: Dayjs | null,
  dateTo: Dayjs | null,
): MainMetricsGroupBy => {
  switch (preset) {
    case 'last_7_days':
    case 'last_30_days':
    case 'current_month':
    case 'last_month':
      return 'day';
    case 'current_year':
    case 'previous_year':
    case 'all_time':
      return 'week';
    case 'custom': {
      if (!dateFrom || !dateTo) return 'day';
      const days = dateTo.startOf('day').diff(dateFrom.startOf('day'), 'day') + 1;
      if (days <= 45) return 'day';
      return 'week';
    }
    default:
      return assertUnreachable(preset);
  }
};
